import { useDropzone } from 'react-dropzone'
import { Upload, X, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useUploadDocumentDialog } from './UploadDocumentDialog.logic'
import { ALLOWED_MIME_TYPES, DOCUMENT_TYPE_OPTIONS } from './UploadDocumentDialog.static'
import { formatFileSize } from '@/pages/documents/DocumentsPage/DocumentsPage.static'

interface UploadDocumentDialogProps {
  open: boolean
  onClose: () => void
  patientId?: string
  staffId?: string
}

export function UploadDocumentDialog({
  open,
  onClose,
  patientId,
  staffId,
}: UploadDocumentDialogProps) {
  const {
    t,
    fileItems,
    fileErrors,
    submitError,
    isLoading,
    onDrop,
    removeFile,
    updateFileItem,
    handleSubmit,
  } = useUploadDocumentDialog({ onClose, patientId, staffId })

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ALLOWED_MIME_TYPES.reduce(
      (acc, type) => {
        acc[type] = []
        return acc
      },
      {} as Record<string, string[]>
    ),
  })

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('uploadDocuments')}</DialogTitle>
          <DialogDescription>{t('uploadDescription')}</DialogDescription>
        </DialogHeader>

        <div className="w-full min-w-0 space-y-4">
          {submitError && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
              {submitError}
            </div>
          )}

          <div
            {...getRootProps()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="text-muted-foreground mb-2 size-8" />
            <p className="text-muted-foreground text-sm">{t('dragDropHint')}</p>
            <p className="text-muted-foreground mt-1 text-xs">{t('allowedTypes')}</p>
            <p className="text-muted-foreground text-xs">{t('maxFileSize', { size: '10MB' })}</p>
          </div>

          {fileErrors.length > 0 && (
            <div className="space-y-1">
              {fileErrors.map((err, i) => (
                <p key={i} className="text-destructive text-xs">
                  {err}
                </p>
              ))}
            </div>
          )}

          {fileItems.length > 0 && (
            <div className="max-h-96 overflow-y-auto space-y-3">
              {fileItems.map(({ file, documentType, description, documentDate }, index) => (
                <div key={`${file.name}-${index}`} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="text-muted-foreground size-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{file.name}</p>
                      <p className="text-muted-foreground text-xs">{formatFileSize(file.size)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeFile(index)}
                      className="size-6 shrink-0"
                      disabled={isLoading}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={documentType}
                      onValueChange={(val) =>
                        updateFileItem(index, { documentType: val as typeof documentType })
                      }
                      disabled={isLoading}
                    >
                      <SelectTrigger className="h-8 w-full text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_TYPE_OPTIONS.map((type) => (
                          <SelectItem key={type} value={type} className="text-xs">
                            {t(`types.${type.toLowerCase()}` as 'types.other')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Input
                      type="date"
                      value={documentDate}
                      onChange={(e) => updateFileItem(index, { documentDate: e.target.value })}
                      disabled={isLoading}
                      className="h-8 text-xs"
                    />
                  </div>

                  <Textarea
                    value={description}
                    onChange={(e) => updateFileItem(index, { description: e.target.value })}
                    placeholder={t('description_field')}
                    rows={2}
                    disabled={isLoading}
                    className="resize-none text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t('cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || fileItems.length === 0}
            >
              {isLoading
                ? t('uploadProgress')
                : fileItems.length > 0
                  ? `${t('uploadDocuments')} (${fileItems.length})`
                  : t('uploadDocuments')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
